/**
 * Water test entry form - extracted from FABDataEntry for reuse.
 *
 * Features:
 * - Parameter chip selection (pH, Sal, Alk, Ca, Mg, NO3, PO4)
 * - Value input with +/- adjustment and target range indicator
 * - Date/time picker, notes, tester brand fields
 * - Error handling with dismissible alert
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Stack,
  Alert,
  Transition,
  TextInput,
  Select,
  Button,
  rem,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  IconCheck,
  IconX,
  IconNotes,
  IconClock,
  IconFlask,
} from '@tabler/icons-react';
import { useTank } from '@/contexts/TankContext';
import {
  useTankTargetRanges,
  useCreateMeasurement,
  useManualEntryParameters,
  useDefaultTesters,
  TESTER_BRANDS,
  type ManualMeasurementRequest,
  type TesterBrand,
} from '@/api/measurements';
import { ParameterChips } from '../FABDataEntry/ParameterChips';
import { ValueInput } from '../FABDataEntry/ValueInput';

export interface WaterTestFormProps {
  /** Callback when entry is successfully saved */
  onSuccess: (
    entry: ManualMeasurementRequest,
    parameterName: string,
    unit: string,
    tester: TesterBrand | null
  ) => void;
  /** Optional callback for error handling */
  onError?: (error: Error) => void;
  /** Pre-select a parameter key (e.g., 'no3') */
  defaultParameterKey?: string;
}

export function WaterTestForm({ onSuccess, onError, defaultParameterKey }: WaterTestFormProps) {
  const { selectedTank } = useTank();

  // Form state
  const [selectedParameter, setSelectedParameter] = useState<string | null>(
    null
  );
  const [value, setValue] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [tester, setTester] = useState<TesterBrand | null>(null);
  const [timestamp, setTimestamp] = useState<Date>(new Date());

  // Track if component is mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Data hooks
  const { data: manualEntryParams } = useManualEntryParameters();
  const { data: targetRangesData } = useTankTargetRanges(selectedTank?.id);
  const createMeasurement = useCreateMeasurement(selectedTank?.id);
  const { data: defaultTestersData } = useDefaultTesters(selectedTank?.id);

  // Parameters list
  const loggableParameters = useMemo(() => {
    if (!manualEntryParams?.parameters) return [];
    return manualEntryParams.parameters;
  }, [manualEntryParams]);

  // Target range for selected parameter
  const targetRange = useMemo(() => {
    if (!selectedParameter || !targetRangesData?.ranges) return null;
    const range = targetRangesData.ranges.find(
      (t) => t.parameterId === selectedParameter
    );
    if (!range) return null;
    return {
      parameter_key: range.parameterId,
      low: range.minValue,
      high: range.maxValue,
      unit: range.unit,
    };
  }, [selectedParameter, targetRangesData]);

  // Selected parameter config
  const selectedParamConfig = useMemo(() => {
    if (!selectedParameter || !manualEntryParams?.parameters) return null;
    return (
      manualEntryParams.parameters.find((p) => p.key === selectedParameter) ||
      null
    );
  }, [selectedParameter, manualEntryParams]);

  // Track if user has explicitly selected a tester for this entry
  const hasUserSelectedTester = useRef(false);

  // Reset form
  const resetForm = useCallback(() => {
    setSelectedParameter(null);
    setValue('');
    setNotes('');
    setTester(null);
    setTimestamp(new Date());
    hasUserSelectedTester.current = false;
  }, []);

  // Set default parameter if provided and not already selected
  useEffect(() => {
    if (defaultParameterKey && !selectedParameter && loggableParameters.length > 0) {
      const found = loggableParameters.find((p) => p.key === defaultParameterKey);
      if (found) {
        setSelectedParameter(defaultParameterKey);
      }
    }
  }, [defaultParameterKey, loggableParameters, selectedParameter]);

  // Prefill tester when parameter selected
  useEffect(() => {
    if (!selectedParameter || hasUserSelectedTester.current) return;
    const defaultTester =
      defaultTestersData?.default_testers?.[selectedParameter];
    if (defaultTester && TESTER_BRANDS.some((b) => b.value === defaultTester)) {
      setTester(defaultTester as TesterBrand);
    }
  }, [selectedParameter, defaultTestersData]);

  // Track when user explicitly changes tester
  const handleTesterChange = (val: string | null) => {
    hasUserSelectedTester.current = true;
    setTester(val as TesterBrand | null);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (
      !selectedParameter ||
      value === '' ||
      !selectedTank ||
      !selectedParamConfig
    )
      return;

    const entry: ManualMeasurementRequest = {
      parameter_key: selectedParameter,
      value: typeof value === 'number' ? value : parseFloat(String(value)),
      recorded_at: timestamp.toISOString(),
      notes: notes.trim() || undefined,
      tester_brand: tester || undefined,
    };

    try {
      await createMeasurement.mutateAsync(entry);
      if (isMountedRef.current) {
        onSuccess(
          entry,
          selectedParamConfig.display_name,
          selectedParamConfig.unit,
          tester
        );
        resetForm();
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Failed to save'));
    }
  };

  if (!selectedTank) return null;

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {createMeasurement.error && (
        <Alert
          icon={<IconX size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => createMeasurement.reset()}
        >
          {createMeasurement.error.message}
        </Alert>
      )}

      {/* Parameter Selection Chips */}
      <ParameterChips
        parameters={loggableParameters}
        selected={selectedParameter}
        onChange={setSelectedParameter}
      />

      {/* Value Input with +/- Buttons */}
      <Transition transition="slide-down" mounted={!!selectedParamConfig}>
        {(styles) => (
          <div style={styles}>
            {selectedParamConfig && (
              <ValueInput
                value={value}
                onChange={setValue}
                paramConfig={selectedParamConfig}
                targetRange={targetRange}
              />
            )}
          </div>
        )}
      </Transition>

      {/* Date/Time Picker */}
      <DateTimePicker
        label="Date & Time"
        placeholder="When was this measured?"
        value={timestamp}
        onChange={(val) => val && setTimestamp(new Date(val))}
        leftSection={<IconClock size={16} />}
        valueFormat="MMM D, YYYY h:mm A"
        clearable={false}
        maxDate={new Date()}
        styles={{
          input: {
            fontSize: rem(16),
          },
        }}
      />

      {/* Notes Field */}
      <TextInput
        label="Notes"
        placeholder="Optional notes about this reading..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        leftSection={<IconNotes size={16} />}
        styles={{
          input: {
            fontSize: rem(16),
          },
        }}
      />

      {/* Tester Selection */}
      <Select
        label="Tester"
        placeholder="Select tester brand (optional)"
        data={TESTER_BRANDS}
        value={tester}
        onChange={handleTesterChange}
        clearable
        leftSection={<IconFlask size={16} />}
        styles={{
          input: {
            fontSize: rem(16),
          },
        }}
      />

      {/* Submit Button */}
      <Button
        fullWidth
        size="md"
        onClick={handleSubmit}
        loading={createMeasurement.isPending}
        disabled={!selectedParameter || value === ''}
        leftSection={<IconCheck size={18} />}
      >
        Save Entry
      </Button>
    </Stack>
  );
}
